/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  call,
  put,
  race,
  take,
  takeLatest,
  takeEvery,
  debounce,
  delay,
  select,
  getContext,
} from "typed-redux-saga";

import { liveRefreshWatcher } from "./liveRefreshSaga";
import {
  allStaticData,
  seriesTypesStaticData,
  timeSeriesLimitsStaticData,
  measurementSeriesValuesStaticData,
  timeSeriesValuesStaticData,
  timeSeriesValuesUpdated,
  jobMetadataFetch,
} from "./staticSaga";
import { pickDefaultMachine } from "./pickDefaultMachineSaga";
import { getType } from "typesafe-actions";
import { rootActions, RootState } from "../";
import { selectIsAuthenticated } from "../selectors";
import { CMSClientType } from "@/cms-api";
import { getGlobalConfigs } from "@/index";
import { latestJobFlow } from "./latestJobFlow";
import {
  fetchFilesDetailsForFileInfoIds,
  downloadFileForSelectedFileId,
  refreshJobSummaryFilesOnPopupOpened,
} from "../global/sagas";
import { data } from "./dataSaga";
import { jobStatisticsWatcher } from "./jobStatisticsWatcher";

export function* machineAnalysisRootSaga() {
  const config = getGlobalConfigs();
  yield* call(pickDefaultMachine);
  yield* debounce(
    config.tabSwitchDebounceInMs,
    getType(rootActions.machineAnalysis.machineAnalysisTabSwitched),
    updateMachineAnalysisTabPreferenceSaga
  );
  while (true) {
    try {
      yield* call(allStaticData);
      yield* race({
        latestJobFlow: call(latestJobFlow),
        liveRefreshWatcher: call(liveRefreshWatcher, {
          delayDuration: config.refreshRateFastInMs,
        }),
        seriesStaticDataWatcher: takeLatest(
          [getType(rootActions.machineAnalysis.jobsListCheckboxClicked)],
          seriesTypesStaticData
        ),
        measurementTypesStaticValuesWatcher: takeLatest(
          [
            getType(
              rootActions.machineAnalysis.measurementTypeSeriesListItemClicked
            ),
            getType(
              rootActions.machineAnalysis.measurementTableMeasurementRowClicked
            ),
            getType(
              rootActions.machineAnalysis.multiJobTableMeasurementTypeClicked
            ),
            getType(rootActions.machineAnalysis.jobsListCheckboxClicked),
            getType(rootActions.machineAnalysis.updateMeasurementSelection),
          ],
          measurementSeriesValuesStaticData
        ),
        timeSeriesStaticValueWatcher: takeLatest(
          [
            getType(
              rootActions.machineAnalysis.timeSeriesTypesSeriesListItemClicked
            ),
          ],
          timeSeriesValuesStaticData
        ),
        timeSeriesSampleRateChangeWatcher: takeLatest(
          [getType(rootActions.machineAnalysis.timeSeriesSampleRateChanged)],
          timeSeriesValuesStaticData
        ),
        UpdateTimeSeriesValueWatcher: takeLatest(
          getType(rootActions.machineAnalysis.timeSeriesValueUpdated),
          timeSeriesValuesUpdated
        ),

        timeSeriesStaticLimitWatcher: takeLatest(
          [
            getType(
              rootActions.machineAnalysis.timeSeriesTypesSeriesListItemClicked
            ),
          ],
          timeSeriesLimitsStaticData
        ),
        jobSummaryPopupFilesTabClickedWatcher: takeLatest(
          getType(rootActions.global.jobSummaryPopupFilesTabClicked),
          fetchFilesDetailsForFileInfoIds
        ),
        jobSummaryPopupOpenedWatcher: takeEvery(
          getType(rootActions.global.popupUpdated),
          refreshJobSummaryFilesOnPopupOpened
        ),
        downloadFileWatcher: takeEvery(
          getType(rootActions.global.jobSummaryPopupDownloadFileButtonClicked),
          downloadFileForSelectedFileId
        ),
        jobMetadataFetchWatcher: takeLatest(
          getType(rootActions.machineAnalysis.jobMetadataFetchRequested),
          jobMetadataFetch
        ),
        jobMeasurementsWatcher: takeLatest(
          getType(rootActions.machineAnalysis.jobMeasurementsRequested),
          data.fetchUniqueJobMeasurements
        ),
        jobStatisticsWatcher: call(jobStatisticsWatcher),
        cancel: take([
          getType(rootActions.session.startEndUpdated),
          getType(rootActions.session.focusedMachineIdUpdated),
          getType(
            rootActions.machineAnalysis.machineSelectorSelectButtonClicked
          ),
        ]),
      });
    } catch (error) {
      console.warn(`Machine Analysis root saga: ${error}`);
    }
    // Clear time series data, we have new parameters
    yield* put(rootActions.machineAnalysis.dataInvalidated());
  }
}

function* updateMachineAnalysisTabPreferenceSaga() {
  try {
    yield* delay(50);

    const isAuthenticated = yield* select(selectIsAuthenticated);
    if (!isAuthenticated) return;

    const client = yield* getContext<CMSClientType>("client");
    const preferences = yield* select(
      (state: RootState) => state.global.preferences
    );
    const machineAnalysisTab = yield* select(
      (state: RootState) => state.userViewPreference.machineAnalysisView
    );

    const response = yield* call(
      (updatedPreferences) =>
        client.preferences.updatePages(updatedPreferences).promise,
      {
        ...preferences,
        machineAnalysisTab,
      }
    );
    response;
  } catch (e) {
    console.error("Failed to save machine analysis tab to database:", e);
  }
}
